import React from 'react';
import { Save, ArrowRight, RefreshCw, Code, Clock, Layers, Lightbulb } from 'lucide-react';

const ConceptCard = ({ concept, onSave, onProceedToBuilder, onRegenerate }) => {
  const complexityColors = {
    simple: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    complex: 'bg-red-100 text-red-800',
  };

  return (
    <div className="bg-white border-2 border-purple-200 rounded-xl p-6 shadow-lg">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Generated App Concept</h3>
          <p className="text-gray-600 text-sm">{concept?.summary}</p>
        </div>
        <div className="flex gap-2">
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${
              complexityColors?.[concept?.complexity] || 'bg-gray-100 text-gray-800'
            }`}
          >
            {concept?.complexity?.charAt(0)?.toUpperCase() + concept?.complexity?.slice(1)}
          </span>
        </div>
      </div>
      {/* Details Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Features */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-gray-900">Key Features</h4>
          </div>
          <ul className="space-y-2">
            {concept?.features?.map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-1.5 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Components */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Code className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-gray-900">React Components</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {concept?.components?.map((component, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium"
              >
                {component}
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* Recommendations */}
      {concept?.recommendations && concept?.recommendations?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-gray-900">Recommendations</h4>
          </div>
          <ul className="space-y-2">
            {concept?.recommendations?.map((rec, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-1.5 flex-shrink-0" />
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Estimated Time */}
      {concept?.estimatedTime && (
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-6 pb-6 border-b border-gray-200">
          <Clock className="w-4 h-4" />
          <span>Estimated build time: {concept?.estimatedTime}</span>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={onProceedToBuilder}
          className="flex-1 min-w-[200px] flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
        >
          <span>Proceed to Visual Builder</span>
          <ArrowRight className="w-5 h-5" />
        </button>
        <button
          onClick={onSave}
          className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          <Save className="w-5 h-5" />
          <span>Save Concept</span>
        </button>
        <button
          onClick={onRegenerate}
          className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          <RefreshCw className="w-5 h-5" />
          <span>Regenerate</span>
        </button>
      </div>
    </div>
  );
};

export default ConceptCard;